'use client'

import { useEffect, useState } from 'react'

import { useTranslations } from '@/lib/next-shim'

import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import type { DialogProps } from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

const MIN_LENGTH = 12
const MAX_LENGTH = 20

type ChangePasswordDialogProps = {
  open: boolean
  onClose: () => void
  onSubmit: (password: string) => Promise<boolean>
}

export function ChangePasswordDialog({ open, onClose, onSubmit }: ChangePasswordDialogProps) {
  const t = useTranslations('component.passwordChangeOtpDialog')

  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setPassword('')
      setConfirmation('')
      setShowPassword(false)
      setShowConfirmation(false)
      setSubmitting(false)
      setError(null)
    }
  }, [open])

  const rules = [
    { key: 'minLength', label: t('password.rules.minLength'), isValid: password.length >= MIN_LENGTH },
    { key: 'maxLength', label: t('password.rules.maxLength'), isValid: password.length <= MAX_LENGTH },
    { key: 'digit', label: t('password.rules.digit'), isValid: /\d/.test(password) },
    { key: 'lowercase', label: t('password.rules.lowercase'), isValid: /[a-z]/.test(password) },
    { key: 'uppercase', label: t('password.rules.uppercase'), isValid: /[A-Z]/.test(password) },
  ]
  const policyValid = rules.every((rule) => rule.isValid)
  const confirmationValid = Boolean(password) && password === confirmation
  const showMismatch = Boolean(confirmation) && !confirmationValid
  const canSubmit = policyValid && confirmationValid

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  const handleDialogClose: DialogProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick') return
    handleClose()
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    const ok = await onSubmit(password)
    setSubmitting(false)
    if (!ok) setError(t('errors.generic'))
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} disableEscapeKeyDown={submitting} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ alignItems: 'center', display: 'flex', gap: 2, justifyContent: 'space-between', pr: 1.5 }}>
        <Box component="span">{t('title.password')}</Box>
        <IconButton aria-label={t('actions.close')} disabled={submitting} edge="end" onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('password.description')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t('password.newPasswordLabel')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            inputProps={{ maxLength: MAX_LENGTH }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? t('password.hidePassword') : t('password.showPassword')}
                    edge="end"
                    onClick={() => setShowPassword((visible) => !visible)}
                    onMouseDown={(event) => event.preventDefault()}
                    size="small"
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label={t('password.confirmPasswordLabel')}
            type={showConfirmation ? 'text' : 'password'}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            error={showMismatch}
            helperText={showMismatch ? t('errors.passwordMismatch') : undefined}
            inputProps={{ maxLength: MAX_LENGTH }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showConfirmation ? t('password.hidePassword') : t('password.showPassword')}
                    edge="end"
                    onClick={() => setShowConfirmation((visible) => !visible)}
                    onMouseDown={(event) => event.preventDefault()}
                    size="small"
                  >
                    {showConfirmation ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {rules.map((rule) => (
              <Typography key={rule.key} component="li" variant="body2" sx={{ color: rule.isValid ? 'success.main' : 'text.secondary', mb: 0.5 }}>
                {rule.label}
              </Typography>
            ))}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disableElevation disabled={submitting} onClick={handleClose} variant="text">
          {t('actions.cancel')}
        </Button>
        <LoadingButton disabled={!canSubmit} loading={submitting} onClick={handleSubmit} variant="contained">
          {t('actions.confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
